import { z } from 'zod'

/**
 * Body validators for the approval action endpoints.
 *
 * Split from `workflow.schema.ts` — that file is the admin-authored
 * workflow-config validator; this file is the per-action HTTP body
 * validator. Different surfaces, different owners.
 */

export const approvalApproveSchema = z.object({
	comment: z.string().max(2000).optional(),
})
export type ApprovalApproveInput = z.infer<typeof approvalApproveSchema>

export const approvalRejectSchema = z.object({
	/**
	 * Reject reasons are mandatory — the approval row reliably captures why.
	 * Trim + non-empty enforced so a whitespace-only comment is rejected
	 * server-side (defence in depth; the UI already disables the button on
	 * `comment.trim().length === 0`).
	 */
	comment: z.string().trim().min(1, 'A rejection comment is required').max(2000),
})
export type ApprovalRejectInput = z.infer<typeof approvalRejectSchema>

export const requestCancelSchema = z.object({
	reason: z.string().max(2000).optional(),
})
export type RequestCancelInput = z.infer<typeof requestCancelSchema>

/**
 * Body for the submitter-only info-gate answer endpoint
 * (`POST /requests/:id/info-gate/:stepId`).
 *
 * `providedData` is a loose key/value map because the allowed keys are
 * determined at runtime by the info-gate's frozen `fieldsSnapshot`. The
 * service layer validates each submitted key against the snapshot before
 * persisting.
 */
export const infoGateAnswerSchema = z.object({
	providedData: z.record(z.string(), z.unknown()),
})
export type InfoGateAnswerInput = z.infer<typeof infoGateAnswerSchema>

/**
 * Body for the approver-only "Request more info" side-loop
 * (`POST /requests/approvals/:id/request-info`).
 *
 * `fields` is what the submitter must fill — same shape as
 * `InfoGateField` from the workflow config, snapshotted onto the
 * `request_info_gates` row at request time so a later workflow edit
 * can't break the open request.
 *
 * `note` is an optional free-text reason shown to the submitter.
 * `dueAt` is an optional ISO timestamp; absent means no SLA on the
 * info-request itself (the parent approval's SLA is paused regardless).
 */
const infoGateFieldSchema = z.object({
	id: z.string().min(1),
	label: z.string().min(1),
	type: z.enum(['text', 'textarea', 'number', 'date', 'checkbox', 'select']),
	required: z.boolean().optional(),
	options: z.array(z.object({ label: z.string(), value: z.string() })).optional(),
})

export const requestInfoSchema = z.object({
	fields: z.array(infoGateFieldSchema).min(1, 'At least one field is required'),
	note: z.string().max(2000).optional(),
	dueAt: z.coerce.date().optional(),
})
export type RequestInfoInput = z.infer<typeof requestInfoSchema>
